/**
 * Iter 67bv — Recurring expense rules + threshold alert settings.
 *
 * Vendors define predictable fixed-cost rules (e.g. monthly insurance premium,
 * yearly registration tax, quarterly parking subscription). Each rule
 * auto-materialises a real `vehicle_expenses` row whenever `next_due_date`
 * passes, so the fleet P&L stays accurate without manual reminders.
 *
 * Renders inside FleetFinanceTab below the expense ledger. Also exposes a
 * threshold settings form so the Fleet Health tile can render alert chips
 * when a car's YTD expenses cross the configured ceiling.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import Money from "@/components/Money";
import {
  Repeat2, Plus, Pencil, Trash2, AlertTriangle, RefreshCw, Save, Loader2, Pause, PlayCircle,
} from "lucide-react";

const CATEGORIES = [
  { value: "maintenance",      label: "Maintenance" },
  { value: "tires",            label: "Tires" },
  { value: "battery",          label: "Battery" },
  { value: "insurance",        label: "Insurance" },
  { value: "registration_tax", label: "Registration / Road tax" },
  { value: "repair",           label: "Repair" },
  { value: "inspection",       label: "Inspection (ITP/MOT)" },
  { value: "cleaning",         label: "Cleaning" },
  { value: "fuel",             label: "Fuel" },
  { value: "parking",          label: "Parking" },
  { value: "fine",             label: "Fine" },
  { value: "other",            label: "Other" },
];
const CADENCE_LABELS = { monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

function _newRuleDraft() {
  return {
    car_id: "",
    category: "insurance",
    label: "",
    amount_eur: "",
    cadence: "monthly",
    next_due_date: new Date().toISOString().slice(0, 10),
  };
}

export default function RecurringExpensesPanel({ cars = [], onChanged }) {
  const [rules, setRules] = useState([]);
  const [settings, setSettings] = useState({ alert_threshold_eur: 0, alert_threshold_pct_of_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(_newRuleDraft());
  const [saving, setSaving] = useState(false);
  const [materialising, setMaterialising] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rR, sR] = await Promise.all([
        api.get("/vendor/expense-rules"),
        api.get("/vendor/expense-settings"),
      ]);
      setRules(rR.data || []);
      setSettings({
        alert_threshold_eur: Number(sR.data?.alert_threshold_eur || 0),
        alert_threshold_pct_of_revenue: Number(sR.data?.alert_threshold_pct_of_revenue || 0),
      });
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load recurring rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const carLabel = (id) => {
    const c = cars.find((x) => x.id === id);
    return c ? `${c.label}${c.plate ? ` · ${c.plate}` : ""}` : id;
  };

  const onOpenCreate = () => {
    setEditing(null);
    setDraft({ ..._newRuleDraft(), car_id: cars[0]?.id || "" });
    setDialogOpen(true);
  };
  const onOpenEdit = (r) => {
    setEditing(r);
    setDraft({
      car_id: r.car_id,
      category: r.category,
      label: r.label || "",
      amount_eur: String(r.amount_eur ?? ""),
      cadence: r.cadence || "monthly",
      next_due_date: (r.next_due_date || "").slice(0, 10),
    });
    setDialogOpen(true);
  };

  const onSave = async () => {
    if (!draft.car_id) { toast.error("Pick a vehicle first."); return; }
    if (!draft.label.trim()) { toast.error("Label is required."); return; }
    if (!draft.amount_eur || Number(draft.amount_eur) <= 0) { toast.error("Amount must be > 0."); return; }
    setSaving(true);
    try {
      const body = {
        car_id: draft.car_id,
        category: draft.category,
        label: draft.label.trim(),
        amount_eur: Number(draft.amount_eur),
        cadence: draft.cadence,
        next_due_date: draft.next_due_date,
      };
      if (editing) {
        const patch = { label: body.label, amount_eur: body.amount_eur, cadence: body.cadence, next_due_date: body.next_due_date };
        await api.patch(`/vendor/expense-rules/${editing.id}`, patch);
        toast.success("Rule updated.");
      } else {
        await api.post("/vendor/expense-rules", body);
        toast.success("Rule created.");
      }
      setDialogOpen(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Delete recurring rule "${r.label}"?`)) return;
    try {
      await api.delete(`/vendor/expense-rules/${r.id}`);
      toast.success("Rule deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err) || "Delete failed."); }
  };

  const onToggleActive = async (r) => {
    try {
      await api.patch(`/vendor/expense-rules/${r.id}`, { is_active: !r.is_active });
      toast.success(r.is_active ? "Rule paused." : "Rule resumed.");
      load();
    } catch (err) { toast.error(formatApiError(err) || "Update failed."); }
  };

  const onRunMaterialise = async () => {
    setMaterialising(true);
    try {
      const { data } = await api.post("/vendor/expense-rules/materialise");
      const n = data?.created || 0;
      toast.success(n > 0 ? `Materialised ${n} expense row${n === 1 ? "" : "s"}.` : "Up to date — nothing due.");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Materialise failed.");
    } finally {
      setMaterialising(false);
    }
  };

  const onSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.patch("/vendor/expense-settings", {
        alert_threshold_eur: Number(settings.alert_threshold_eur || 0),
        alert_threshold_pct_of_revenue: Number(settings.alert_threshold_pct_of_revenue || 0),
      });
      toast.success("Alert thresholds saved.");
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed.");
    } finally {
      setSavingSettings(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-8" data-testid="recurring-expenses-panel">
      {/* Alert thresholds */}
      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
            </div>
            <div>
              <div className="font-semibold text-sm">Expense alert thresholds</div>
              <div className="text-xs text-neutral-500">Trigger Fleet Health warnings when a car crosses these ceilings.</div>
            </div>
          </div>
        </div>
        <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-xs">Hard ceiling per car (€/year)</Label>
            <Input
              type="number" step="50" min="0"
              value={settings.alert_threshold_eur}
              onChange={(e) => setSettings({ ...settings, alert_threshold_eur: e.target.value })}
              className="mt-1 tabular-nums"
              placeholder="0 = disabled"
              data-testid="alert-threshold-eur"
            />
            <p className="text-[11px] text-neutral-500 mt-1">e.g. 1200 → flag any car that crosses €1,200 YTD.</p>
          </div>
          <div>
            <Label className="text-xs">Soft ceiling — % of revenue</Label>
            <Input
              type="number" step="0.05" min="0" max="2"
              value={settings.alert_threshold_pct_of_revenue}
              onChange={(e) => setSettings({ ...settings, alert_threshold_pct_of_revenue: e.target.value })}
              className="mt-1 tabular-nums"
              placeholder="0 = disabled"
              data-testid="alert-threshold-pct"
            />
            <p className="text-[11px] text-neutral-500 mt-1">0.7 → flag when expenses ≥ 70% of revenue.</p>
          </div>
          <div>
            <Button
              onClick={onSaveSettings}
              disabled={savingSettings}
              className="rounded-full bg-amber-600 hover:bg-amber-700"
              data-testid="alert-threshold-save"
            >
              {savingSettings ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save thresholds
            </Button>
          </div>
        </div>
      </div>

      {/* Recurring rules */}
      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Repeat2 className="h-4 w-4 text-indigo-700" />
            </div>
            <div>
              <div className="font-semibold text-sm">Recurring expense rules</div>
              <div className="text-xs text-neutral-500">Auto-create expense rows on schedule — insurance premiums, road tax, parking…</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onRunMaterialise}
              disabled={materialising || rules.length === 0}
              className="rounded-full"
              data-testid="recurring-materialise"
            >
              {materialising ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Run due now
            </Button>
            <Button
              onClick={onOpenCreate}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={cars.length === 0}
              data-testid="recurring-add"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add rule
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recurring-rules-table">
            <thead className="bg-neutral-50 border-b border-border text-[10px] uppercase tracking-wider text-neutral-600">
              <tr>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3 w-36">Category</th>
                <th className="text-left px-4 py-3">Label</th>
                <th className="text-right px-4 py-3 w-32">Amount</th>
                <th className="text-left px-4 py-3 w-32">Cadence</th>
                <th className="text-left px-4 py-3 w-36">Next due</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && rules.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-neutral-500">
                    No recurring rules yet. Add one to automate predictable costs (e.g. monthly insurance premium).
                  </td>
                </tr>
              )}
              {!loading && rules.map((r) => {
                const overdue = r.is_active && r.next_due_date && r.next_due_date <= today;
                return (
                  <tr key={r.id} className="border-b border-border last:border-0 hover:bg-neutral-50/60" data-testid={`recurring-rule-${r.id}`}>
                    <td className="px-4 py-3">{carLabel(r.car_id)}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 uppercase tracking-wider text-[10px] font-bold">
                        {CATEGORIES.find((c) => c.value === r.category)?.label || r.category}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">{r.label}</td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums"><Money amount={r.amount_eur} /></td>
                    <td className="px-4 py-3">{CADENCE_LABELS[r.cadence] || r.cadence}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {r.next_due_date}
                      {overdue && <Badge className="ml-2 bg-rose-600 text-[10px]">Due</Badge>}
                      {!r.is_active && <Badge variant="secondary" className="ml-2 text-[10px]">Paused</Badge>}
                    </td>
                    <td className="px-2 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onToggleActive(r)} title={r.is_active ? "Pause rule" : "Resume rule"} data-testid={`recurring-toggle-${r.id}`}>
                        {r.is_active ? <Pause className="h-3.5 w-3.5 text-neutral-600" /> : <PlayCircle className="h-3.5 w-3.5 text-emerald-600" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenEdit(r)} data-testid={`recurring-edit-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5 text-neutral-600" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => onDelete(r)} data-testid={`recurring-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="sm:max-w-lg" data-testid="recurring-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat2 className="h-5 w-5 text-indigo-600" />
              {editing ? "Edit recurring rule" : "New recurring rule"}
            </DialogTitle>
            <DialogDescription>The rule materialises a real expense row each cycle, starting from the next due date.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Vehicle</Label>
              <Select value={draft.car_id} onValueChange={(v) => setDraft({ ...draft, car_id: v })} disabled={!!editing}>
                <SelectTrigger className="mt-1" data-testid="recurring-dialog-car"><SelectValue placeholder="Pick a vehicle…" /></SelectTrigger>
                <SelectContent>
                  {cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}{c.plate ? ` · ${c.plate}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                <SelectTrigger className="mt-1" data-testid="recurring-dialog-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cadence</Label>
              <Select value={draft.cadence} onValueChange={(v) => setDraft({ ...draft, cadence: v })}>
                <SelectTrigger className="mt-1" data-testid="recurring-dialog-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Label</Label>
              <Input
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="e.g. Comprehensive insurance premium"
                className="mt-1"
                data-testid="recurring-dialog-label"
              />
            </div>
            <div>
              <Label>Amount (€)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={draft.amount_eur}
                onChange={(e) => setDraft({ ...draft, amount_eur: e.target.value })}
                className="mt-1 tabular-nums"
                data-testid="recurring-dialog-amount"
              />
            </div>
            <div>
              <Label>Next due date</Label>
              <Input
                type="date"
                value={draft.next_due_date}
                onChange={(e) => setDraft({ ...draft, next_due_date: e.target.value })}
                className="mt-1"
                data-testid="recurring-dialog-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={onSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="recurring-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : (editing ? <Pencil className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />)}
              {editing ? "Save changes" : "Create rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
